import React, { FC, useCallback, useState } from 'react';
import { Modal, Form, Input, Button, notification } from 'src/common';
import { useTranslation } from 'react-i18next';
import { useForm } from 'antd/lib/form/Form';
import LearnMoreLink from 'src/shared/LearnMoreLink';
import {
  getUpdateCacheOnCreateCustomBidFields,
  useCreateCustomBidField
} from 'src/models/company/CustomBidFields/hooks';
import { TenderBoxFieldType } from '@tendium/prom-types/tender';

interface IForm {
  name: string;
}

interface IProps {
  type: TenderBoxFieldType;
  onClose: () => void;
}

export const CreateCustomBidFieldModal: FC<IProps> = props => {
  const { onClose, type } = props;
  const [form] = useForm<IForm>();
  const { t, i18n } = useTranslation();

  const [onCreate, { loading }] = useCreateCustomBidField();

  const onSave = useCallback(() => {
    form.validateFields().then(({ name }) => {
      if (!name) return;

      onCreate({
        variables: {
          type,
          name
        },
        update: getUpdateCacheOnCreateCustomBidFields()
      })
        .then(() => {
          onClose();
        })
        .catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
    });
  }, [form, onClose, onCreate, t, type]);

  const [isDisabled, setIsDisabled] = useState(true);

  const onValuesChange = useCallback(
    (changedValues: Partial<IForm>, values: IForm) => {
      setIsDisabled(!values.name);
    },
    [setIsDisabled]
  );

  return (
    <Modal
      title={<span>{t('CustomBidFields.addNewBidField')}</span>}
      visible
      onCancel={onClose}
      footer={
        <>
          <Button type={'text'} onClick={onClose}>
            {t('Common.cancel')}
          </Button>

          <Button type={'primary'} onClick={onSave} loading={loading} disabled={isDisabled}>
            {t('Common.save')}
          </Button>
        </>
      }
    >
      <Form form={form} layout={'vertical'} onValuesChange={onValuesChange}>
        <Form.Item
          name={'name'}
          label={t('Common.name')}
          rules={[{ max: 35, message: t('Common.inputTooLong', { inputName: 'Name' }) }]}
        >
          <Input placeholder={t('Common.name')} />
        </Form.Item>
      </Form>
      <LearnMoreLink
        title={t('CustomBidFields.readMoreAboutBidFields')}
        href={`${import.meta.env.VITE_HELPDOCS_DOMAIN}${i18n.language}/article/uxidsao60c-anbudsfalt`}
      />
    </Modal>
  );
};

export default CreateCustomBidFieldModal;
